import { analyticsEvent } from "@sd-utility/analytics";
import { saveForGoogleAnalyticsToSessionStorage } from "client/api/google-analytics-service";
import { addRecentSearch } from "client/api/recent-searches-service";
import {
  ANALYTICS_DIRECT_INPUT,
  DEBOUNCED_TIME_MS,
} from "client/utils/constants";
import { sanitizeInput } from "client/utils/helpers";
import debounce from "lodash.debounce";
import React, { useCallback, useMemo } from "react";
import {
  SearchBar,
  SearchFieldProps,
  SearchFieldSubmitHandler,
} from "../search-field/search-field";

export interface SearchChangeEventHandler {
  (e: React.ChangeEvent<HTMLInputElement>, term?: string): Promise<void>;
}

export interface SearchInputProps {
  onChange: SearchChangeEventHandler;
  brand: string;
}

const CONSTRUCTOR_PROPS: Partial<SearchFieldProps> = {
  "data-cnstrc-search-input": true,
  componentsProps: {
    form: { "data-cnstrc-search-form": true },
    button: { "data-cnstrc-search-submit-btn": true },
  },
};

export const submitSearchTerm = (
  e: React.FormEvent<HTMLFormElement>,
  searchTerm: string | null,
) => {
  e.preventDefault();
  if (searchTerm && searchTerm.length > 0) {
    const sanitizedSTerm = sanitizeInput(searchTerm);
    addRecentSearch(sanitizedSTerm);
    saveForGoogleAnalyticsToSessionStorage(ANALYTICS_DIRECT_INPUT);
    window.location.assign(`/search/${sanitizedSTerm}`);
  }
};

const SearchInput = ({ brand, onChange }: SearchInputProps) => {
  const placeHolderStr = `Search ${
    brand.charAt(0).toUpperCase() + brand.slice(1)
  }`.replace("ireland", "");

  // memoize the debounce call with useMemo
  const debouncedOnChange = useMemo(() => {
    return debounce(onChange, DEBOUNCED_TIME_MS);
  }, [onChange]);

  const handleChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      await debouncedOnChange(e, e.target.value);
    },
    [debouncedOnChange],
  );

  const handleSubmit = useCallback<SearchFieldSubmitHandler>(
    (e, searchTerm) => {
      if (!searchTerm) return e.preventDefault();
      submitSearchTerm(e, searchTerm);
    },
    [],
  );

  const handleClick = useCallback(() => {
    analyticsEvent({
      action: "Header",
      category: "Real Estate",
      label: "Search",
    });
  }, []);

  const handleKeyUp = useCallback<React.KeyboardEventHandler<HTMLInputElement>>(
    (event) => {
      if (event.key === "Enter") {
        analyticsEvent({
          action: "Search",
          category: "UX",
          label: "Keystroke",
        });
      }
    },
    [],
  );

  const handleSubmitButtonClick = useCallback<
    React.MouseEventHandler<HTMLButtonElement>
  >((event) => {
    // detect whether it was a genuine mouse click, and not triggered by keypress enter
    if (event.detail !== 0) {
      analyticsEvent({
        action: "Search",
        category: "UX",
        label: "Icon",
      });
    }
  }, []);

  return (
    <SearchBar
      {...CONSTRUCTOR_PROPS}
      dataTestId="desktop-header-search"
      placeholder={placeHolderStr}
      formId="header-search"
      onChange={handleChange}
      onClick={handleClick}
      onKeyUp={handleKeyUp}
      onSubmit={handleSubmit}
      onSubmitButtonClick={handleSubmitButtonClick}
    />
  );
};

export default SearchInput;
